
import { supabase } from "@/integrations/supabase/client";
import { QueryBuilder } from "./graphql/queryBuilder";
import { HomeContentResponse, QueryOptions } from "./graphql/types";

export class GraphQLService {
  static async fetchHomeContent(limit = 8): Promise<HomeContentResponse> {
    console.log("Fetching home content with GraphQL-like service");
    
    try {
      // Define field selections
      const queries = {
        channels: new QueryBuilder('channels')
          .select({
            id: true,
            name: true,
            thumbnail: true,
            logo_light: true,
            logo_dark: true,
            logo: true
          })
          .order('name'),
          
        playlists: new QueryBuilder('playlists')
          .select({
            id: true,
            name: true,
            thumbnail: true,
            channels: {
              name: true
            }
          })
          .order('name'),
          
        popularClips: new QueryBuilder('clips_with_source')
          .select({
            id: true,
            title: true,
            thumbnail: true,
            duration: true,
            playlist_name: true,
            video_url: true
          })
          .order('downloads', { ascending: false })
          .limit(limit),
          
        recentClips: new QueryBuilder('clips_with_source')
          .select({
            id: true,
            title: true,
            thumbnail: true,
            duration: true,
            playlist_name: true,
            video_url: true
          })
          .order('created_at', { ascending: false })
          .limit(limit),
          
        derivatives: new QueryBuilder('derivatives_with_details')
          .select({
            id: true,
            title: true,
            thumbnail: true,
            video_url: true,
            creator_name: true,
            creator_avatar: true,
            expression_type: true,
            aspect_ratio: true,
            status: true,
            submitted_at: true,
            views: true,              // Make sure we're selecting these
            likes_count: true,        // Make sure we're selecting these
            favorites_count: true,    // Make sure we're selecting these
            comments_count: true      // Make sure we're selecting these
          })
          .eq('status', 'approved')
          .order('approved_at', { ascending: false })
          .limit(limit)
      };

      // Execute all queries in parallel
      const [
        channels,
        playlists,
        popularClips,
        recentClips,
        derivativesResult
      ] = await Promise.all([
        queries.channels.execute(),
        queries.playlists.execute(),
        queries.popularClips.execute(),
        queries.recentClips.execute(),
        queries.derivatives.execute()
      ]);
      
      // Add extra logging for derivatives
      if (derivativesResult && derivativesResult.length > 0) {
        console.log("GraphQL service - first derivative received from DB:", {
          id: derivativesResult[0].id,
          title: derivativesResult[0].title,
          views: derivativesResult[0].views,
          likes_count: derivativesResult[0].likes_count,
          favorites_count: derivativesResult[0].favorites_count,
          comments_count: derivativesResult[0].comments_count
        });
      }
      
      return {
        channels: channels || [],
        playlists: playlists || [],
        popularClips: popularClips || [],
        recentClips: recentClips || [],
        derivatives: derivativesResult || [],
        challenges: [] // Will be populated from static data
      };
    } catch (error) {
      console.error("Error in GraphQL-like fetch:", error);
      return {
        channels: [],
        playlists: [],
        popularClips: [],
        recentClips: [],
        derivatives: [],
        challenges: []
      };
    }
  }
  
  /**
   * Fetches clips for a specific playlist with minimal fields
   */
  static async fetchPlaylistClips(playlistId: string) {
    return new QueryBuilder('clips_with_source')
      .select({
        id: true,
        title: true,
        thumbnail: true,
        duration: true,
        playlist_name: true,
        video_url: true
      })
      .eq('playlist_id', playlistId)
      .order('created_at', { ascending: false })
      .execute();
  }
  
  /**
   * Fetches channel details with minimal fields
   */
  static async fetchChannelDetails(channelId: string) {
    return new QueryBuilder('channels')
      .select({
        id: true,
        name: true,
        thumbnail: true,
        logo_light: true,
        logo_dark: true,
        description: true
      })
      .eq('id', channelId)
      .execute();
  }
}
